import { Box, Stack, styled, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ScoreService } from "src/context/EntityBasedScore/ScoreService";
import { PropertyMulti } from "src/context/Property/Builder/PropertyBuilder";
import { deserializeReadOnly } from "src/context/Property/PropertyValueSerializer";
import { useScoreDetailContext } from "src/context/ScoreDetail/useScoreDetailContext";
import i18n from "src/i18n";
import { formatNumber } from "src/utils/formatting";

type DisplayMultiProps = {
    property: PropertyMulti;
};
export const DisplayMulti = (props: DisplayMultiProps) => {
    const { property } = props;
    const { scoreList } = useScoreDetailContext();
    const { lastSelected } = scoreList;
    const { t } = useTranslation();
    const theme = useTheme();

    const score = property.score?.value;
    return (
        <Stack padding={theme.spacing(4)}>
            <MultiTable property={property} unit={lastSelected.unit} precision={lastSelected.precision} />
            <Stack flexDirection={"row"} alignItems={"center"} gap={theme.spacing(1)} marginTop={theme.spacing(2)}>
                <Typography variant="h5">{t("questionnaire.score_total")}:</Typography>{" "}
                {score !== undefined && score !== null && (
                    <Stack flexDirection={"row"} gap={theme.spacing(0.5)}>
                        <Typography variant="number-small">{formatNumber(score, i18n.resolvedLanguage)}</Typography>
                        <Typography variant="body-medium">{lastSelected.unit}</Typography>
                    </Stack>
                )}
            </Stack>
        </Stack>
    );
};

type MultiTableProps = {
    property: PropertyMulti;
    unit: string | undefined;
    precision: number | undefined;
};
const MultiTable = (props: MultiTableProps) => {
    const { property, unit, precision = 0 } = props;
    const { t } = useTranslation();
    const theme = useTheme();

    const headers =
        property.property.children?.flatMap((p, colIdx) => {
            return [
                {
                    id: colIdx,
                    title: p.title,
                },
            ];
        }) ?? [];

    const rows = property.content;
    return (
        <BoxStyled>
            <table>
                <thead>
                    <tr>
                        {headers.map((header) => {
                            return (
                                <th key={header.id}>
                                    <Typography variant="h5">{header.title}</Typography>
                                </th>
                            );
                        })}
                        <th key="score">
                            <Typography variant="h5">{t("Score")}</Typography>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => {
                        const _rowScore = row.reduce<number | null>((acc, r) => {
                            const newValue = ScoreService.addScore(acc, r.score?.value ?? null);
                            return newValue;
                        }, null);
                        const rowScore = ScoreService.normalizeScore(_rowScore, precision).value;
                        return (
                            <tr key={i}>
                                {row.map((question, j) => {
                                    const { labels } = deserializeReadOnly(question);
                                    return (
                                        <td key={j}>
                                            {Array.isArray(labels) ?
                                                <Stack>
                                                    {labels.map((v) => (
                                                        <Typography variant="body-medium">{v}</Typography>
                                                    ))}
                                                </Stack>
                                            :   <Typography variant="body-medium">{labels}</Typography>}
                                        </td>
                                    );
                                })}
                                <td key="score">
                                    <Stack flexDirection={"row"} gap={theme.spacing(0.5)}>
                                        <Typography variant="number-small">{rowScore}</Typography>
                                        <Typography variant="body-medium">{unit}</Typography>
                                    </Stack>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </BoxStyled>
    );
};

const BoxStyled = styled(Box)(({ theme }) => ({
    overflowX: "auto",
    width: "100%",
    "& table": {
        // Looks like this Option makes the border Radius impossible.
        borderCollapse: "collapse",
        border: `1px solid ${theme.custom.grey[500]}`,
        width: "100%",
        background: theme.custom.grey[100],
        "& th": {
            padding: "8px",
            border: `1px dashed ${theme.custom.grey[500]}`,
            background: theme.custom.grey[100],
            textAlign: "left",
        },
        "& td": {
            textAlign: "left",
            padding: "8px",
            border: `1px dashed ${theme.custom.grey[500]}`,
            background: theme.custom.grey[100],
        },
    },
}));
